import styled from "@emotion/styled";
import React from "react";
import ChevronRightIcon from "@ecologi/assets/icons/chevron-right.svg";
import EcologiLogo from "@ecologi/assets/icons/ecologi-logo-fill-colour.svg";
import TrolleyIcon from "@ecologi/assets/icons/trolley-icon.svg";
import { Link } from "../Link";
import type { EcologiTheme } from "../theme";

type ThemeColorKey = Exclude<keyof EcologiTheme["colors"], "opacity">;

export interface ImpactShopHeaderProps {
  backgroundColor?: ThemeColorKey;
  basketItemCount?: number;
  isLoggedIn?: boolean;
  pageTitle?: string;
  sticky?: boolean;
}

const Header = styled.header<{
  backgroundColor?: ThemeColorKey;
  sticky?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ sticky = true }) =>
    sticky &&
    `
    position: sticky;
    top: 0;
  `}
  z-index: 500;
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor ? theme.colors[backgroundColor] : theme.colors.snowdrop};
  border-bottom: ${({ backgroundColor }) =>
    backgroundColor ? "none" : "1px solid rgba(0, 0, 0, 0.1)"};
  padding: 24px 24px 17px;

  @media ${({ theme }) => theme.bp.desktopUp} {
    flex-direction: row;
    gap: 40px;
    padding: 32px 48px 25px;
  }
`;

const Inner = styled.div<{ isLoggedIn?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: ${({ isLoggedIn }) => (isLoggedIn ? "76px" : "0")};

  @media ${({ theme }) => theme.bp.tabletUp} {
    padding-right: 0;
  }

  @media ${({ theme }) => theme.bp.desktopUp} {
    justify-content: flex-start;
    gap: 40px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forestDark};
    outline-offset: 2px;
  }
`;

const Logo = styled(EcologiLogo)`
  height: 48px;
  width: 100px;
  color: ${({ theme }) => theme.colors.forest};
`;

const DesktopBreadcrumb = styled.nav`
  display: none;
  align-items: center;
  gap: 6px;
  flex: 1;

  @media ${({ theme }) => theme.bp.desktopUp} {
    display: flex;
  }
`;

const MobileBreadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  @media ${({ theme }) => theme.bp.desktopUp} {
    display: none;
  }
`;

const BreadcrumbLink = styled(Link)`
  font-family: ${({ theme }) => theme.typography.fontStack};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.forest};
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

const BreadcrumbText = styled.span`
  font-family: ${({ theme }) => theme.typography.fontStack};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.28;
  color: ${({ theme }) => theme.colors.charcoalLight};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BreadcrumbChevron = styled(ChevronRightIcon)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

const RightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SignInLink = styled(Link)`
  font-family: ${({ theme }) => theme.typography.fontStack};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.forest};
  text-decoration: none;
  padding: 8px 16px;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

const BasketButton = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-radius: 60px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.forestDark};

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forestDark};
    outline-offset: 2px;
  }
`;

const CountBadge = styled.span`
  position: absolute;
  top: 0;
  left: 28px;
  width: 28px;
  height: 28px;
  border-radius: 1000px;
  background-color: ${({ theme }) => theme.colors.jungleLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.typography.fontStack};
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.forestDark};
`;

export function ImpactShopHeader({
  backgroundColor,
  basketItemCount = 0,
  isLoggedIn,
  pageTitle,
  sticky = true,
}: ImpactShopHeaderProps) {
  return (
    <Header sticky={sticky} backgroundColor={backgroundColor}>
      <Inner isLoggedIn={isLoggedIn}>
        <LogoLink
          href="/"
          title="Go to the Ecologi homepage"
          data-testid="logo-link"
        >
          <Logo aria-label="Ecologi" />
        </LogoLink>
        <DesktopBreadcrumb aria-label="Breadcrumb">
          {pageTitle && (
            <>
              <BreadcrumbLink href="/impact-shop" data-testid="breadcrumb-link">
                Impact shop
              </BreadcrumbLink>
              <BreadcrumbChevron aria-hidden="true" />
              <BreadcrumbText>{pageTitle}</BreadcrumbText>
            </>
          )}
        </DesktopBreadcrumb>
        <RightSlot>
          {!isLoggedIn && (
            <SignInLink href="/api/login" data-testid="sign-in-link">
              Sign in
            </SignInLink>
          )}
          <BasketButton
            href="/impact-shop/basket"
            aria-label={`Basket${
              basketItemCount > 0 ? ` ${basketItemCount} items` : ""
            }`}
            data-testid="mini-basket"
          >
            <TrolleyIcon />
            {basketItemCount > 0 && <CountBadge>{basketItemCount}</CountBadge>}
          </BasketButton>
        </RightSlot>
      </Inner>
      {pageTitle && (
        <MobileBreadcrumb aria-label="Breadcrumb">
          <BreadcrumbLink href="/impact-shop">Impact shop</BreadcrumbLink>
          <BreadcrumbChevron aria-hidden="true" />
          <BreadcrumbText>{pageTitle}</BreadcrumbText>
        </MobileBreadcrumb>
      )}
    </Header>
  );
}
